/**
 * demoApi.js — API functions for the V2 Demo Panel page.
 *
 * Replaces V1 ApiRequester calls with V2 api client functions + direct fetch.
 */

import { v4 as uuidv4 } from 'uuid';
import { apiFetch, apiSave } from '../../../services/api.js';
import {
    campusToGuid, getUTCTimestamp, getLocalTimeWithOffset, getLuxonUTCTime,
    convertHHMMtoSec,
} from '../../../libs.jsx';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN,
});

// ---------------------------------------------------------------------------
// READ — Reference Data
// ---------------------------------------------------------------------------

/**
 * Fetch all residents with their devices for a campus.
 * V1: ApiRequester myfetch → sp_dev_getallresidentsanddevicesbycampusid
 */
export function fetchResidentsAndDevices(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getallresidentsanddevicesbycampusid',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/**
 * Fetch all areas with their devices for a campus.
 * V1: ApiRequester myfetch → sp_dev_getallareadevicesbycampusid
 */
export function fetchAreasAndDevices(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getallareadevicesbycampusid',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/**
 * Fetch all event categories.
 * V1: ApiRequester myfetch → fetchall att_event_categories
 */
export function fetchEventCategories(campusId) {
    return apiFetch({
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
        table: 'att_event_categories',
    });
}

// ---------------------------------------------------------------------------
// WRITE — Alarm Create / Clear
// ---------------------------------------------------------------------------

/**
 * Create alarm via v2Send POST endpoint.
 * V1: ApiRequester({ reqEndpoint: "v2Send", Method: "POST", Payload })
 * @returns {Promise<[boolean, string?]>}
 */
export async function createAlarm(payload) {
    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_DEMO_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        const data = await resp.json();
        return data?.status === 'success' ? [true] : [false, 'Failed to create alarm'];
    } catch (err) {
        console.error('[demoApi] createAlarm error:', err);
        return [false, 'Request failed'];
    }
}

/**
 * Clear alarm via v2Send PATCH endpoint.
 * V1: ApiRequester({ reqEndpoint: "v2Send", Method: "PATCH", Payload })
 * @returns {Promise<[boolean, string?]>}
 */
export async function clearAlarm(payload) {
    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_DEMO_ENDPOINT, {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify(payload),
        });
        const data = await resp.json();
        return data?.status === 'success' ? [true] : [false, 'Failed to clear alarm'];
    } catch (err) {
        console.error('[demoApi] clearAlarm error:', err);
        return [false, 'Request failed'];
    }
}

// ---------------------------------------------------------------------------
// WRITE — Rounds / Check-In
// ---------------------------------------------------------------------------

/**
 * Create a rounds record.
 * V1: ApiRequester({ reqEndpoint: "mysave", Tbl: "core_rounds", urlType: "PUT", ... })
 */
export function createRounds(payload, campusId, userId, clientId) {
    return apiSave({
        table: 'core_rounds',
        payload,
        pKey: 'rounds_id',
        condition: 'primary',
        campusId,
        clientId,
        userId,
        method: 'PUT',
    });
}

/**
 * Create a check-in record.
 * V1: ApiRequester({ reqEndpoint: "mysave", Tbl: "core_checkin", urlType: "PUT", ... })
 */
export function createCheckin(payload, campusId, userId, clientId) {
    return apiSave({
        table: 'core_checkin',
        payload,
        pKey: 'checkin_id',
        condition: 'primary',
        campusId,
        clientId,
        userId,
        method: 'PUT',
    });
}

// ---------------------------------------------------------------------------
// SIMULATE — Escalation Careflow
// ---------------------------------------------------------------------------

/**
 * Run careflow simulation for a device.
 * V1: ApiRequester({ reqEndpoint: "simulate", campus_id, device_id, timeofday, runtime, event_category_id })
 * @returns {Promise<Array>} — array of { timestamp, handler, to }
 */
export async function simulateCareflow({ campusId, deviceId, timeofday, runtime, eventCategoryId, careflowV2 }) {
    const body = {
        campus_id: campusId,
        device_id: deviceId,
        timeofday,
        runtime,
        event_category_id: eventCategoryId
    };
    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_SIM_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });
        const data = await resp.json();
        return data?.Status === 'OK' ? (data.Content || []) : [];
    } catch (err) {
        console.error('[demoApi] simulateCareflow error:', err);
        return [];
    }
}

// ---------------------------------------------------------------------------
// PAYLOAD BUILDERS
// ---------------------------------------------------------------------------

/**
 * Build a V2 alarm payload (matching V1 getV2 function).
 * @returns {object} — alarm payload for createAlarm
 */
export function buildAlarmPayload({ dataType, resident, area, device, serverId, eventType, session, campusId, residentData }) {
    const timestamp = getUTCTimestamp();
    let residents, locationId, locationName;

    if (dataType === 'resident') {
        residents = [{
            resident_id: resident.resident_id,
            firstName: resident.resident_first_name,
            lastName: resident.resident_last_name,
        }];
        locationId = resident.area_id;
        locationName = resident.area_name;
    } else {
        locationId = area.area_id;
        locationName = area.area_name;
        residents = (residentData || [])
            .filter((r) => r.area_id === area.area_id)
            .map((r) => ({
                resident_id: r.resident_id,
                firstName: r.resident_first_name,
                lastName: r.resident_last_name,
            }));
    }

    return {
        id: uuidv4(), type: eventType, stage: 'CREATE',
        CampusId: campusToGuid(session, campusId),
        text: `Alarm ${device.device_name} in ${locationName}`,
        capCodes: ['0220001'],
        location: { id: locationId, name: locationName, building: 'Main', uid: '' },
        device: { id: device.device_id, name: device.device_name, type: '', area: '', presses: 1, uid: device.manufacture_uuid },
        residents, near: [], escalation: [0], capcodes: '0220001', vendor: 'PalatiumCare',
        version: '4.0', eVersion: '2.51', ttl: 0, iTime: 0, eventDateTime: timestamp, ackDateTime: '',
        timeZone: session?.campusData?.[campusId]?.tz_offset, actions: '', messageNumber: '', mac: serverId,
    };
}

/**
 * Build a rounds/check-in payload for apiSave.
 * @returns {Array} — single-element array payload for apiSave
 */
export function buildRoundsPayload({ deviceId, residentId, areaId, campusId, tzOffset }) {
    return [{
        device_id: deviceId,
        resident_id: residentId,
        area_id: areaId,
        campus_id: campusId,
        local_create_time: getLocalTimeWithOffset(tzOffset),
        create_time: getLuxonUTCTime(),
    }];
}
